import { LoadingButton } from '@mui/lab'
import { Button, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { OverlayState } from '@/hooks/useOverlay'
import { Document } from '@/types/documents'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import SplitDocumentOptions from './SplitDocumentOptions'
import SplitDocumentImages from './SplitDocumentPages'
import useSplitDocuments from './useSplitDocuments'

type SplitDocumentDialogProps = {
  document: Document
  documentQueryKey?: QueryKey
  overlay: OverlayState
}

export default function SplitDocumentDialog({
  document,
  documentQueryKey,
  overlay,
}: SplitDocumentDialogProps) {
  const data = useSplitDocuments({
    document,
    documentQueryKey,
  })

  const { isPending, splitPoints, createDocuments } = data

  const onClose = () => {
    overlay.close()
  }

  return (
    <Dialog
      title="Split Document"
      maxWidth="xl"
      fullWidth={false}
      {...overlay}
      onClose={onClose}
    >
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            Click between the page images below to choose where to split the
            document. Each section will become a new document.
          </Typography>
          <SplitDocumentImages document={document} splitDocumentsData={data} />
          <Stack alignItems="center">
            <SplitDocumentOptions
              pages={document.document_pages}
              splitDocumentsData={data}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mr: 'auto' }}
        >
          This document will not be deleted after splitting.
        </Typography>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <LoadingButton
          disabled={!splitPoints.length}
          variant="contained"
          onClick={() => {
            createDocuments().then(() => {
              overlay.close()
            })
          }}
          loading={isPending}
        >
          Split
        </LoadingButton>
      </DialogFooter>
    </Dialog>
  )
}
